import React, { useEffect, useState } from 'react';
import { MinusCircle, Share2 } from 'lucide-react';
import { updateDoc, doc, query, collection, where, getDocs, getDoc } from "firebase/firestore";
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { createTransaction, setAccountBalanceByUid, getAccountBalanceByUid } from '../database/Functions';
import { AlertModal } from "./ui/alert-modal"
import { getTeamLogoWithFallback } from "../utils/teamLogoUtils"

function UserBet({ bet, onBetDeleted }) {
  const { currentUser, userProfile } = useAuth();
  
  // Determine the other user's ID based on bet status
  const otherUserId = bet.status === "Created"
    ? currentUser.uid // not accepted yet, so show current user
    : bet.user_id === currentUser.uid 
        ? bet.bet_acceptor_uid 
        : bet.user_id;

  const [otherUser, setOtherUser] = useState({
    username: '',
    full_name: '',
    profile_picture: 'https://img.freepik.com/premium-photo/3d-style-avatar-profile-picture-featuring-male-character-generative-ai_739548-13626.jpg', // fallback
  });
  const [copiedModalOpen, setCopiedModalOpen] = useState(false)
  const [copiedLink, setCopiedLink] = useState("")

  // Fetch other user's information
  useEffect(() => {
    const fetchOtherUser = async () => {
      try {
        // For Created bets, no need to fetch from DB
        if (bet.status === "Created") {
          const preferredName =
            userProfile?.username ||
            userProfile?.full_name ||
            currentUser.displayName ||
            currentUser.email?.split("@")[0] ||
            currentUser.email;
          setOtherUser({
            username: preferredName,
            full_name: userProfile?.full_name || preferredName,
            profile_picture:
              userProfile?.profile_picture ||
              currentUser.photoURL ||
              'https://img.freepik.com/premium-photo/3d-style-avatar-profile-picture-featuring-male-character-generative-ai_739548-13626.jpg',
          });
          return;
        }

        const userDoc = await getDoc(doc(db, 'users', otherUserId));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setOtherUser({
            username: data.username || data.full_name || data.displayName,
            full_name: data.full_name || data.displayName,
            profile_picture: data.profile_picture || data.photoURL || 'https://img.freepik.com/premium-photo/3d-style-avatar-profile-picture-featuring-male-character-generative-ai_739548-13626.jpg',
          });
        }
      } catch (error) {
        console.log('Error fetching other user:', error);
      }
    };

    fetchOtherUser();
  }, [otherUserId, bet.status, currentUser, userProfile]);

  const deleteBet = async () => {
    try {
      const uniqueID = bet["uniqueId"];
      const uid = currentUser.uid;

      // Query to find the bet in Firestore
      const q = query(collection(db, "ActiveBets"), where("uniqueId", "==", uniqueID));
      const querySnapshot = await getDocs(q);

      // Loop through the documents found by the query
      querySnapshot.forEach(async (betDoc) => {
        // Update the bet status to "Deleted"
        await updateDoc(doc(db, "ActiveBets", betDoc.id), {
          status: "Deleted",
        });

        // Update the user's account balance
        const userAccountBalance = await getAccountBalanceByUid(uid);
        const betWagerAmount = parseFloat(bet.wager_amount);
        const afterBalance = userAccountBalance + betWagerAmount;

        // Log the transaction
        createTransaction(uid, "Wager", "Delete Wager", userAccountBalance, afterBalance, betWagerAmount, bet);

        // Add the wager amount back to the user's balance
        setAccountBalanceByUid(uid, afterBalance);

        // Notify parent component of bet deletion
        if (onBetDeleted) {
          onBetDeleted(bet.id);
        }

        alert('Bet Deleted');
      });
    } catch (error) {
      console.error("Error deleting bet:", error);
    }
  };

  let winText = `To Win: $${bet.payout}`;

  if (bet.status === "Settled") {
    if (bet.winner === currentUser.uid) {
      winText = `Won: $${bet.payout}`;
    } else {
      winText = `Lost: $${bet.wager_amount}`;
    }
  }


  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return {
        day: date.toLocaleDateString('en-US', { 
          weekday: 'short',
        }),
        month: date.toLocaleDateString('en-US', { 
          month: 'short',
        }),
        dayNum: date.toLocaleDateString('en-US', { 
          day: '2-digit',
        }),
        year: date.getFullYear(),
        time: date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }),
        timezone: 'CDT'
      };
    } catch (error) {
      return {
        day: 'Invalid',
        month: 'Invalid',
        dayNum: 'XX',
        year: 'XXXX',
        time: '12:00PM',
        timezone: 'CDT'
      };
    }
  };

  const formattedDate = formatDate(bet.bet_time);
  const isMyBet = currentUser.uid === bet.user_id;
  const myPick = isMyBet ? bet.user_pick : (bet.user_pick === bet.home_team ? bet.away_team : bet.home_team);
  const sportKey = bet.bet_sport_key || bet.sport_key
  const homeLogo = getTeamLogoWithFallback(bet.home_team, sportKey)
  const awayLogo = getTeamLogoWithFallback(bet.away_team, sportKey)

  return (
    <div className="rounded-xl overflow-hidden bg-gray-900/50 backdrop-blur-sm mb-4 flex flex-col border-0 md:border md:border-gray-700 hover:border-emerald-500/30 transition-all duration-300">
      {/* Header */}
      <div className="grid grid-cols-3 items-center p-3 md:p-4 border-b border-gray-700/50">
        <div className="flex items-center">
        {bet.status === "Created" && (
              <button 
                onClick={deleteBet}
                className="bg-red-500/20 p-1 md:p-1.5 rounded-full hover:bg-red-500/30 transition-colors"
              >
                <MinusCircle size={16} className="text-red-400 md:hidden" />
                <MinusCircle size={18} className="text-red-400 hidden md:block" />
              </button>
            )}
          <span className="text-white text-xs md:text-sm font-medium pr-1 md:pr-2">{bet.bet_sport_title}</span>
        
        </div>
        <div className="text-white text-xs text-center">
          {formattedDate.day} {formattedDate.month} {formattedDate.dayNum} {formattedDate.year}
        </div>
        <div className="text-white text-xs md:text-sm text-right">
          {formattedDate.time} CDT
        </div>
      </div>

      {/* User and Odds */}
      <div className="p-3 md:p-4 bg-gray-800/30 backdrop-blur-sm">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 md:gap-3">
            <img
              src={otherUser.profile_picture}
              alt="User avatar"
              className="w-6 h-6 md:w-8 md:h-8 rounded-full border border-gray-700"
            />
            <span className="text-white text-xs md:text-sm font-medium">{otherUser.username}</span>
          </div>
          <span className="text-white text-xs md:text-sm text-center">{bet.user_pick}</span>
          <span className="text-emerald-400 font-semibold text-xs md:text-sm">-120</span>
        </div>
      </div>

      <div className="border-t border-gray-700/30 mx-4"></div>

      {/* Teams */}
      <div className="p-3 md:p-4 bg-gray-800/30 backdrop-blur-sm flex-grow flex flex-col justify-center">
        {/* Home Team */}
        <div className="grid grid-cols-3 items-center mb-3 md:mb-4">
          <div className="flex items-center gap-1 md:gap-2">
            <div className="w-1 h-3 md:h-4 bg-green-500 rounded-full"></div>
            <span className="text-white text-xs md:text-sm">Home</span>
          </div>
          <div className="flex items-center justify-center gap-2 min-w-0">
            <img
              src={homeLogo}
              alt={bet.home_team}
              className="w-5 h-5 rounded-full bg-gray-900/60 border border-gray-700 object-contain p-0.5 shrink-0"
              onError={(e) => {
                e.currentTarget.onerror = null
                e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(bet.home_team)}&background=1f2937&color=ffffff&size=96`
              }}
            />
            <span className="text-white text-xs md:text-sm text-center font-medium truncate">{bet.home_team}</span>
          </div>
          <div className="flex justify-end">
            {myPick === bet.home_team && 
              <div className="bg-emerald-500/20 text-emerald-400 px-3 md:px-4 py-1 rounded-full text-xs md:text-sm font-medium">
                Selected
              </div>
            }
          </div>
        </div>

        {/* Away Team */}
        <div className="grid grid-cols-3 items-center">
          <div className="flex items-center gap-1 md:gap-2">
            <div className="w-1 h-3 md:h-4 bg-blue-400 rounded-full"></div>
            <span className="text-white text-xs md:text-sm">Away</span>
          </div>
          <div className="flex items-center justify-center gap-2 min-w-0">
            <img
              src={awayLogo}
              alt={bet.away_team}
              className="w-5 h-5 rounded-full bg-gray-900/60 border border-gray-700 object-contain p-0.5 shrink-0"
              onError={(e) => {
                e.currentTarget.onerror = null
                e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(bet.away_team)}&background=1f2937&color=ffffff&size=96`
              }}
            />
            <span className="text-white text-xs md:text-sm text-center font-medium truncate">{bet.away_team}</span>
          </div>
          <div className="flex justify-end">
            {myPick === bet.away_team &&  
              <div className="bg-emerald-500/20 text-emerald-400 px-3 md:px-4 py-1 rounded-full text-xs md:text-sm font-medium">
                Selected
              </div>
            }
          </div>
        </div>
      </div>

      {/* Wager Footer */}
      <div className={`p-2 md:p-3 flex justify-between items-center ${
        bet.status === "Settled" 
          ? (bet.winner === currentUser.uid ? "bg-emerald-500/90" : "bg-red-500/90") 
          : "bg-emerald-500/90"
      }`}>
        <span className="text-white text-xs md:text-sm font-medium">Wager: ${bet.wager_amount}</span>
        <div className="flex items-center">
          <span className={`text-white text-xs md:text-sm font-medium`}>
            {winText}
          </span>
          {bet.status === "Created" && (
            <button
              className="ml-2 md:ml-4 bg-emerald-600 hover:bg-emerald-700 p-1 md:p-1.5 rounded-full transition-colors"
              onClick={() => {
                const shareUrl = `https://www.rivalssportsbook.com/betdetails?id=${bet.uniqueId}`;
                navigator.clipboard.writeText(shareUrl);
                setCopiedLink(shareUrl)
                setCopiedModalOpen(true)
              }}
            >
              <Share2 size={14} className="text-white md:hidden" />
              <Share2 size={16} className="text-white hidden md:block" />
            </button>
          )}
        </div>
      </div>
      <AlertModal
        open={copiedModalOpen}
        title="Link Copied"
        description={`Your bet invite link has been copied to your clipboard. Share it with friends so they can accept this bet.\n\n${copiedLink}`}
        onClose={() => setCopiedModalOpen(false)}
      />
    </div>
  );
}

export default UserBet;
